<template>
  <!-- HEADER Story: CD-2, CD-3-->
  <div class="w-full border-b border-grey-light-200 bg-white fixed top-0 left-0 right-0 z-20">
    <navbar-component
      :show-mobile-sidebar="showMobileSidebar"
      :show-desktop-sidebar="showDesktopSidebar"
      :notifications="notifications"
      @open-mobile-sidebar="showMobileSidebar = true"
      @close-mobile-sidebar="showMobileSidebar = false"
      @open-desktop-sidebar="showDesktopSidebar = true"
      @close-desktop-sidebar="showDesktopSidebar = false"
      @sign-out="onSignOut"
    />
  </div>

  <!-- MOBILE SIDEBAR  --->
  <mobile-sidebar
    v-if="isMobile || isTablet"
    :open="showMobileSidebar"
    @close="showMobileSidebar = false"
    @sign-out="onSignOut"
  />

  <!-- DESKTOP SIDEBAR  --->
  <desktop-sidebar
    v-if="isDesktop"
    :open="showDesktopSidebar"
    :fullName="fullName"
    :email="email"
    @close="showDesktopSidebar = false"
    @sign-out="onSignOut"
  />

  <!-- MAIN CONTENT -->
  <div
    class="p-4 mt-[72px] min-h-screen"
    :class="{ 'ml-[85px] pr-[20px]': isDesktop, 'pl-[250px]': showDesktopSidebar && isDesktop }"
  >
    <router-view :key="$route.fullPath" />
  </div>

  <!-- OVERLAY -->
  <div
    v-if="showMobileSidebar"
    class="fixed top-[72px] bottom-16 left-0 right-0 z-40 bg-gray-100/80"
    @click="showMobileSidebar = false"
  />

  <!-- MOBILE BOTTOM NAVIGATION -->
  <mobile-bottom-nav v-if="isMobile || isTablet" />
</template>

<script setup lang="ts">
import MobileBottomNav from '@/components/shopper/BottomNav.vue';
import DesktopSidebar from '@/components/shopper/DesktopSidebar.vue';
import MobileSidebar from '@/components/shopper/MobileSidebar.vue';
import NavbarComponent from '@/components/shopper/NavbarComponent.vue';
import type { NavbarNotification } from '@/components/shopper/NavbarComponent.vue';
import { fetchOrders } from '@/services/orders';
import { useUserStore } from '@/stores/users';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ModelOrderResponse } from '@layup/api-sdk';

const { isMobile, isDesktop, isTablet } = useBreakpoint();
const userStore = useUserStore();
const router = useRouter();
const route = useRoute();

const showMobileSidebar = ref(false);
const showDesktopSidebar = ref(true);
const fullName = ref('Layup Customer');
const email = ref('N/A');
const notifications = ref<NavbarNotification[]>([]);

const VALID_STATES = new Set(['PLACED', 'CANCELLING', 'COMPLETED']);

const hasValidRequest = (order: ModelOrderResponse) =>
  order.orderCancellationRequest !== 'REJECTED' ||
  order.orderRefundRequest === 'ACCEPTED' ||
  order.orderRefundRequest === 'REJECTED';

const buildNotifications = (orders: ModelOrderResponse[], userId?: string): NavbarNotification[] =>
  orders.reduce<NavbarNotification[]>((result, order) => {
    const lastNote = order.notifications?.at(-1);
    if (
      lastNote &&
      VALID_STATES.has(order.state!) &&
      hasValidRequest(order) &&
      lastNote.userId !== userId &&
      !lastNote.read
    ) {
      result.push({
        id: result.length,
        change: lastNote.change,
        date: lastNote.date,
        name: lastNote.name,
        reason: lastNote.reason,
        details: lastNote.details,
        orderName: order.name,
        orderId: order._id,
        orderRef: order.reference,
        read: lastNote.read ?? false,
        state: order.state,
        orderRefundRequest: order.orderRefundRequest,
        type: lastNote.type,
        orderType: order.type,
      });
    }
    return result;
  }, []);

onMounted(async () => {
  const user = userStore.user;
  if (user?.name) {
    fullName.value = user.name;
  }
  if (user?.email) {
    email.value = user.email;
  }

  await loadNotifications();
});

const loadNotifications = async () => {
  try {
    const { orders } = await fetchOrders({ populate: ['plans'] });
    notifications.value = buildNotifications(orders || [], userStore.user?._id);
  } catch {
    notifications.value = [];
  }
};

watch(() => route.fullPath, loadNotifications);

const onSignOut = async () => {
  await userStore.signOut();
  await router.push({ name: 'shopperSignIn' });
};
</script>
